import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { money } from '../utils/money';
import './JqCommissionGuide.css';

const API_BASE = '/api';
const rm = (value) => money(Number(value) || 0);
const monthLabel = (month) => new Date(`${month}-01`).toLocaleDateString('en-MY', { month: 'short', year: 'numeric' });
const fmtDate = (value) => new Date(value).toLocaleDateString('en-MY', {
  timeZone: 'Asia/Kuala_Lumpur', year: 'numeric', month: 'short', day: 'numeric'
});

export default function ZenCommissionGuide() {
  const { isSuperadmin } = useAuth();
  const [year, setYear] = useState('2026');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/zen/commission?year=${year}`, { headers: headers() });
      setData(response.data);
      setError('');
    } catch (requestError) {
      setError('Failed to load commission data');
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const release = async (id) => {
    const value = window.prompt('Enter the FINAL actual contract value for this event:');
    if (value == null) return;
    try {
      await axios.put(`${API_BASE}/zen-bookings/${id}/deliver`, { actual_final_value: value }, { headers: headers() });
      await load();
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Release failed');
    }
  };

  if (!data) {
    return <div className="jqc-page"><h1>Commission Guide — Zen</h1>{error && <div className="jqc-error">{error}</div>}<p className="jqc-muted">Loading…</p></div>;
  }

  const tierPct = (rate) => `${(parseFloat(rate) * 100).toFixed(2).replace(/\.00$/, '.0')} %`;
  const tierRange = (tier) => {
    const min = parseFloat(tier.revenue_min);
    const max = parseFloat(tier.revenue_max);
    if (min === 0) return `< ${rm(200000)}`;
    return `${rm(min)} – ${rm(max)}`;
  };

  return (
    <div className="jqc-page">
      <div className="jqc-head">
        <h1>Commission Guide — Zen</h1>
        <label className="jqc-muted">Year:{' '}
          <select value={year} onChange={(event) => setYear(event.target.value)}>
            <option value="2026">2026</option>
            <option value="2027">2027</option>
          </select>
        </label>
      </div>
      {error && <div className="jqc-error">{error}</div>}

      <div className="jqc-kpis jqc-kpis-4">
        <div className="jqc-card"><div className="label">70% Captured (YTD)</div><div className="value">{rm(data.ytd.captured70)}</div></div>
        <div className="jqc-card"><div className="label">30% Settled (YTD)</div><div className="value">{rm(data.ytd.settled30)}</div></div>
        <div className="jqc-card"><div className="label">Total Commission (YTD)</div><div className="value">{rm(data.ytd.total)}</div></div>
        <div className="jqc-card"><div className="label">Total Earned (incl. basic)</div><div className="value">{rm(data.ytd.totalEarned)}</div></div>
      </div>

      <div className="jqc-section-title">Monthly Commission ({data.year})</div>
      <div className="report-table-scroll" role="region" aria-label="Zen monthly commission" tabIndex={0}>
        <table className="jqc-table">
          <thead><tr><th>Month</th><th>70% Captured</th><th>30% Settled</th><th>Commission</th><th>Basic Salary</th><th>Total Earned</th></tr></thead>
          <tbody>
            {data.months.map(month => (
              <tr key={month.month}>
                <td>{monthLabel(month.month)}</td>
                <td>{rm(month.captured70)}</td>
                <td>{rm(month.settled30)}</td>
                <td>{rm(month.total)}</td>
                <td>{month.basicSalary != null ? rm(month.basicSalary) : '—'}</td>
                <td>{month.totalEarned != null ? rm(month.totalEarned) : '—'}</td>
              </tr>
            ))}
            <tr className="total">
              <td>TOTAL</td><td>{rm(data.ytd.captured70)}</td><td>{rm(data.ytd.settled30)}</td>
              <td>{rm(data.ytd.total)}</td><td>{rm(data.ytd.basicSalary)}</td><td>{rm(data.ytd.totalEarned)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="jqc-note">
        Effective August 2026. Wedding, Private Event and Corporate bookings. One rate applies to the full month's booked value.
        70% counts after admin verification; the balance settles on delivery using the locked rate, including any clawback.
      </div>

      <div className="jqc-section-title">Pending Settlements — event date passed</div>
      <div className="jqc-pending">
        <div className="report-table-scroll" role="region" aria-label="Zen pending settlements" tabIndex={0}>
          <table className="jqc-table" style={{ marginBottom: 0 }}>
            <thead><tr><th>Event Date</th><th>Invoice</th><th>Venue</th><th>Forecast Value</th><th>70% Paid</th><th>Locked Rate</th><th></th></tr></thead>
            <tbody>
              {data.pending.length ? data.pending.map(booking => (
                <tr key={booking.id}>
                  <td>{fmtDate(booking.event_date)}</td><td>{booking.invoice_number || '—'}</td><td>{booking.venue || '—'}</td>
                  <td>{rm(booking.forecast_contract_value)}</td><td>{rm(booking.paid_70_percent)}</td>
                  <td>{(parseFloat(booking.forecast_tier_rate) || 0).toFixed(2)}%</td>
                  <td>{isSuperadmin
                    ? <button className="jqc-btn" onClick={() => release(booking.id)}>Release settlement</button>
                    : <span className="jqc-muted">awaiting admin release</span>}</td>
                </tr>
              )) : <tr><td colSpan={7} className="jqc-muted">No verified balances are due.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="jqc-ref">
        <div>
          <div className="jqc-section-title">Tier Rate Guide</div>
          <div className="report-table-scroll" role="region" aria-label="Zen tier rates" tabIndex={0}>
            <table className="jqc-table">
              <thead><tr><th>Cumulative eligible value</th><th>Rate</th></tr></thead>
              <tbody>{data.tiers.map(tier => (
                <tr key={tier.tier_level}><td>{tierRange(tier)}</td><td>{tierPct(tier.rate)}</td></tr>
              ))}</tbody>
            </table>
          </div>
          <div className="jqc-note">Values above RM449,999 are outside the supplied tiers and calculate at 0%.</div>
        </div>
      </div>
    </div>
  );
}
